"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { ArrowRight } from "@/components/icons";
import SectionHeader from "@/components/ui/section-header";
import { trackEvent } from "@/lib/posthog-client";

interface RelatedLink {
  label: string;
  href: string;
}

interface Props {
  fromPage: string;
  blogLinks: RelatedLink[];
  useCaseLinks?: RelatedLink[];
}

function LinkList({
  title,
  links,
  onLinkClick,
}: {
  title: string;
  links: RelatedLink[];
  onLinkClick: (label: string, href: string) => void;
}) {
  return (
    <div className="border-border bg-card rounded-xl border p-6">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              onClick={() => onLinkClick(link.label, link.href)}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-sm transition-colors"
            >
              {link.label}
              <ArrowRight size={13} aria-hidden="true" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function RelatedContent({ fromPage, blogLinks, useCaseLinks }: Props) {
  const prefersReducedMotion = useReducedMotion();

  function handleLinkClick(label: string, href: string) {
    trackEvent("related_content_clicked", { from: fromPage, label, href });
  }

  return (
    <section
      className="py-section border-border border-b"
      aria-labelledby="related-content-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={prefersReducedMotion ? {} : { opacity: 1, y: 0 }}
          viewport={scrollRevealViewport}
          transition={{ duration: 0.5 }}
        >
          <SectionHeader
            eyebrow="Related reading"
            id="related-content-heading"
            heading="Go deeper"
          />

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <LinkList title="From the blog" links={blogLinks} onLinkClick={handleLinkClick} />
            {useCaseLinks && useCaseLinks.length > 0 && (
              <LinkList
                title="Related use cases"
                links={useCaseLinks}
                onLinkClick={handleLinkClick}
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
